import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Zap, X, Check, AlertTriangle, Sparkles, 
  MessageSquare, Mic, Brain, CreditCard, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Credit balance hook
export const useAICredits = () => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCredits = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/ai-credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredits(res.data);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();
  }, []);

  return { credits, loading, refetch: fetchCredits };
};

// Use credits function with upsell handling
export const useCredits = async (amount, action, details = {}) => {
  try {
    const token = localStorage.getItem('session_token');
    const res = await axios.post(`${API}/ai-credits/use`, {
      credits: amount,
      action,
      details
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    return { success: true, data: res.data };
  } catch (error) {
    if (error.response?.status === 402) {
      // Insufficient credits - return upsell data
      return { 
        success: false, 
        needsUpsell: true, 
        upsellData: error.response.data.detail 
      };
    }
    throw error;
  }
};

// Credit packages data - B2C
// Costes estimados por crédito: ~0.005€ (OpenAI GPT-4)
// Margen objetivo: >60% (preferiblemente >80%)
const B2C_PACKAGES = [
  {
    id: 'starter_100',
    name: 'Starter',
    credits: 100,
    price: 2.99,
    costPerCredit: 0.03,
    margin: 83, // 83% margen
    description: '100 créditos de IA',
    popular: false
  },
  {
    id: 'popular_300',
    name: 'Popular',
    credits: 300,
    price: 7.99,
    costPerCredit: 0.027,
    margin: 86, // 86% margen
    description: '300 créditos de IA',
    popular: true,
    savings: '10% ahorro'
  },
  {
    id: 'pro_1000',
    name: 'Pro',
    credits: 1000,
    price: 19.99,
    costPerCredit: 0.02,
    margin: 90, // 90% margen
    description: '1000 créditos de IA',
    popular: false,
    savings: '33% ahorro'
  },
  {
    id: 'mega_3000',
    name: 'Mega',
    credits: 3000,
    price: 49.99,
    costPerCredit: 0.017,
    margin: 91, // 91% margen
    description: '3000 créditos de IA',
    popular: false,
    savings: '44% ahorro'
  }
];

// Credit packages data - B2B (para instituciones - mayores volúmenes, mejores precios)
const B2B_PACKAGES = [
  {
    id: 'b2b_5000',
    name: 'Institución Básico',
    credits: 5000,
    price: 79.99,
    costPerCredit: 0.016,
    margin: 68,
    description: '5.000 créditos para tu academia',
    minStudents: 10
  },
  {
    id: 'b2b_20000',
    name: 'Institución Pro',
    credits: 20000,
    price: 249.99,
    costPerCredit: 0.0125,
    margin: 75,
    description: '20.000 créditos para tu academia',
    popular: true,
    minStudents: 50,
    savings: '22% ahorro'
  },
  {
    id: 'b2b_100000',
    name: 'Institución Enterprise',
    credits: 100000,
    price: 999.99,
    costPerCredit: 0.01,
    margin: 80,
    description: '100.000 créditos para tu academia',
    minStudents: 200,
    savings: '37% ahorro'
  },
  {
    id: 'b2b_500000',
    name: 'Institución Mega',
    credits: 500000,
    price: 3999.99,
    costPerCredit: 0.008,
    margin: 84,
    description: '500.000 créditos para tu academia',
    minStudents: 500,
    savings: '50% ahorro'
  }
];

// Upsell Modal Component
export const CreditUpsellModal = ({ 
  isOpen, 
  onClose, 
  currentBalance = 0,
  requiredCredits = 0,
  message = '',
  onPurchaseSuccess
}) => {
  const [selectedPackage, setSelectedPackage] = useState('popular_300');
  const [purchasing, setPurchasing] = useState(false);

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/ai-credits/purchase`, {
        package_id: selectedPackage,
        payment_method: 'stripe'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`¡${res.data.message}!`);
      if (onPurchaseSuccess) {
        onPurchaseSuccess(res.data);
      }
      onClose();
    } catch (error) {
      toast.error('Error al procesar la compra');
    } finally {
      setPurchasing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Créditos IA agotados</h2>
                <p className="text-purple-200 text-sm">
                  {message || `Necesitas ${requiredCredits - currentBalance} créditos más para continuar`}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Current balance */}
          <div className="mt-4 flex items-center gap-6">
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-purple-200">Tu saldo</p>
              <p className="text-2xl font-bold">{currentBalance}</p>
            </div>
            {requiredCredits > 0 && (
              <div className="bg-red-500/20 rounded-lg px-4 py-2">
                <p className="text-xs text-red-200">Necesitas</p>
                <p className="text-2xl font-bold">{requiredCredits}</p>
              </div>
            )}
          </div>
        </div>

        {/* Packages */}
        <div className="p-6">
          <p className="text-slate-600 text-sm mb-4">Elige un paquete para seguir usando el Tutor IA:</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {B2C_PACKAGES.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`relative rounded-xl p-4 text-left transition-all border-2 ${
                  selectedPackage === pkg.id 
                    ? 'border-purple-500 bg-purple-50 shadow-lg' 
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> Popular
                  </span>
                )}
                
                <p className="font-bold text-slate-900">{pkg.name}</p>
                <p className="text-2xl font-black text-purple-600 mt-1">{pkg.price}€</p>
                <p className="text-xs text-slate-500">
                  {pkg.credits > 0 ? `${pkg.credits.toLocaleString()} créditos` : 'Ilimitado/mes'}
                </p>
                <p className="text-xs text-slate-400 mt-1">{pkg.description}</p>
                
                {pkg.savings && (
                  <span className="text-xs text-emerald-600 font-medium">{pkg.savings}</span>
                )}
                
                {selectedPackage === pkg.id && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* What you get */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-700 mb-2">Con créditos IA puedes:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <MessageSquare className="w-4 h-4 text-purple-500" />
                Chat con tutor (5 créditos)
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Mic className="w-4 h-4 text-purple-500" />
                Tutor con voz (15 créditos)
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Generar flashcards (2 créd.)
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Brain className="w-4 h-4 text-purple-500" />
                Explicar preguntas (3 créd.)
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-6 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={purchasing}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {purchasing ? (
                'Procesando...'
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Comprar créditos
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Low Balance Warning Component (inline)
export const LowBalanceWarning = ({ balance, onBuyClick }) => {
  if (balance >= 20) return null;
  
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
      balance < 5 
        ? 'bg-red-50 border border-red-200' 
        : 'bg-amber-50 border border-amber-200'
    }`}>
      <AlertTriangle className={`w-5 h-5 ${balance < 5 ? 'text-red-500' : 'text-amber-500'}`} />
      <div className="flex-1">
        <p className={`text-sm font-medium ${balance < 5 ? 'text-red-700' : 'text-amber-700'}`}>
          {balance < 5 
            ? `¡Solo te quedan ${balance} créditos!` 
            : `Créditos bajos: ${balance} restantes`}
        </p>
        <p className="text-xs text-slate-500">Recarga para seguir usando el Tutor IA</p>
      </div>
      <Button 
        size="sm" 
        onClick={onBuyClick}
        className={balance < 5 ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
      >
        Recargar
      </Button>
    </div>
  );
};

// Credit Display Badge (for header/navbar)
export const CreditBadge = ({ onClick }) => {
  const { credits, loading } = useAICredits();
  
  if (loading) return null;
  
  const balance = credits?.balance ?? 0;
  const isUnlimited = credits?.is_unlimited;
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
        isUnlimited 
          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
          : balance < 20 
            ? 'bg-red-100 text-red-700 hover:bg-red-200'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      <Zap className="w-4 h-4" />
      {isUnlimited ? '∞' : balance}
    </button>
  );
};

export default CreditUpsellModal;
